/* FLOW: collection & search filtering without page reloads.
   Each change fetches the same page with ?section_id= and swaps the results. Two forms post the
   same fields: the desktop bar (pill buttons with panels) and the phone drawer; whichever changed
   is the one read. Open panels and drawer groups stay as the customer left them across renders. */

class CollectionResults extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.cache = new Map();

    // a control may sit outside its form (the phone sort list uses form="…")
    const formOf = (el) => el.closest('[data-facets-form]') || (el.form?.matches('[data-facets-form]') ? el.form : null);
    this.addEventListener('change', (e) => {
      const form = formOf(e.target);
      // price fields wait for the pause in typing (below); everything else applies at once
      if (form && !e.target.matches('.facet__price input')) this.onFormChange(form);
    });
    this.addEventListener('input', window.Flow.debounce((e) => {
      const form = e.target.closest('[data-facets-form]');
      if (form && e.target.matches('.facet__price input')) this.onFormChange(form);
    }, 600));
    this.addEventListener('click', (e) => {
      const link = e.target.closest('[data-facet-link]');
      if (link) {
        e.preventDefault();
        this.render(new URL(link.href, window.location.origin).searchParams.toString());
        return;
      }
      const more = e.target.closest('[data-load-more-link]');
      if (more) {
        e.preventDefault();
        this.loadMore(more);
      }
    });

    // panels: one open at a time; a click elsewhere or Escape closes them
    this.addEventListener('toggle', (e) => {
      const d = e.target;
      if (!d.matches?.('.facet') || !d.open) return;
      this.querySelectorAll('.facet[open]').forEach((o) => o !== d && (o.open = false));
    }, true);
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.facet')) this.querySelectorAll('.facet[open]').forEach((d) => (d.open = false));
    });
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const open = this.querySelector('.facet[open]');
      if (!open) return;
      open.open = false;
      open.querySelector('summary')?.focus();
    });

    window.addEventListener('popstate', (e) => {
      if (e.state?.facets !== undefined) this.render(e.state.facets, false);
    });
  }

  onFormChange(form) {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form).entries()) {
      if (value !== '') params.append(key, value);
    }
    this.render(params.toString());
  }

  async fetchSection(query) {
    const url = `${window.location.pathname}?${query ? query + '&' : ''}section_id=${this.sectionId}`;
    if (!this.cache.has(url)) this.cache.set(url, fetch(url).then((r) => r.text()));
    return window.Flow.parseHTML(await this.cache.get(url));
  }

  async render(query, push = true) {
    const results = this.querySelector('[data-results]');
    results?.classList.add('is-loading');
    try {
      const doc = await this.fetchSection(query);
      const fresh = doc.querySelector('[data-facets-container]');
      const current = this.querySelector('[data-facets-container]');
      if (!fresh || !current) return;

      // remember what was open: the bar's panel, the drawer and its groups
      const openPanel = this.querySelector('.facet[open]')?.dataset.facet;
      const drawer = this.querySelector('drawer-panel');
      const drawerOpen = drawer?.hasAttribute('open');
      const groups = new Map([...this.querySelectorAll('.facets-drawer__group')].map((d) => [d.dataset.facet || 'sort', d.open]));

      current.querySelector('[data-facets-toolbar]')?.replaceWith(fresh.querySelector('[data-facets-toolbar]'));
      const oldActive = current.querySelector('[data-facets-active]');
      const newActive = fresh.querySelector('[data-facets-active]');
      if (oldActive && newActive) oldActive.replaceWith(newActive);
      else if (oldActive) oldActive.remove();
      else if (newActive) current.querySelector('[data-facets-toolbar]').after(newActive);

      // the drawer element stays (it may be open); only its form and footer are swapped
      const freshDrawer = fresh.querySelector('drawer-panel');
      if (drawer && freshDrawer) {
        drawer.querySelector('[data-facets-form]').innerHTML = freshDrawer.querySelector('[data-facets-form]').innerHTML;
        drawer.querySelector('.drawer__footer').innerHTML = freshDrawer.querySelector('.drawer__footer').innerHTML;
        drawer.querySelectorAll('.facets-drawer__group').forEach((d) => {
          const was = groups.get(d.dataset.facet || 'sort');
          if (was !== undefined) d.open = was;
        });
        if (drawerOpen) drawer.setAttribute('open', '');
      }
      if (openPanel) {
        const panel = this.querySelector(`.facet[data-facet="${CSS.escape(openPanel)}"]`);
        if (panel) panel.open = true;
      }
      results.replaceWith(fresh.querySelector('[data-results]'));

      if (push) window.history.pushState({ facets: query }, '', `${window.location.pathname}${query ? '?' + query : ''}`);
    } finally {
      this.querySelector('[data-results]')?.classList.remove('is-loading');
    }
  }

  async loadMore(link) {
    link.classList.add('is-loading');
    const url = new URL(link.href, window.location.origin);
    const doc = await this.fetchSection(url.searchParams.toString());
    const grid = this.querySelector('[data-product-grid]');
    const freshGrid = doc.querySelector('[data-product-grid]');
    if (grid && freshGrid) {
      const firstNew = freshGrid.firstElementChild;
      grid.append(...freshGrid.children);
      firstNew?.querySelector('a')?.focus({ preventScroll: true });
    }
    const block = this.querySelector('[data-load-more]');
    const freshBlock = doc.querySelector('[data-load-more]');
    if (freshBlock) block.replaceWith(freshBlock);
    else block.remove();
  }
}
customElements.define('collection-results', CollectionResults);

/* Price range: two thumbs on one track, kept in step with the two number fields. The fields carry
   the names Shopify reads; a thumb at its end leaves its field empty (no limit on that side). */
class PriceRange extends HTMLElement {
  connectedCallback() {
    this.max = Number(this.dataset.max) || 0;
    this.track = this.querySelector('.facet__range');
    this.fromRange = this.querySelector('[data-range-from]');
    this.toRange = this.querySelector('[data-range-to]');
    this.fromField = this.querySelector('[data-price-from]');
    this.toField = this.querySelector('[data-price-to]');
    if (!this.fromRange || !this.toRange) return;

    const fromRanges = () => {
      let a = Number(this.fromRange.value), b = Number(this.toRange.value);
      if (a > b) [a, b] = [b, a];
      this.fromField.value = a > 0 ? a : '';
      this.toField.value = b < this.max ? b : '';
      this.paint(a, b);
    };
    const fromFields = () => {
      const a = Number(this.fromField.value) || 0;
      const b = this.toField.value === '' ? this.max : Number(this.toField.value);
      this.fromRange.value = a;
      this.toRange.value = b;
      this.paint(a, b);
    };
    [this.fromRange, this.toRange].forEach((r) => {
      r.addEventListener('input', fromRanges);
      // a thumb let go: tell the form, the same way typing does
      r.addEventListener('change', () => this.fromField.dispatchEvent(new Event('input', { bubbles: true })));
    });
    [this.fromField, this.toField].forEach((f) => f.addEventListener('input', fromFields));
  }

  paint(a, b) {
    if (!this.max) return;
    this.track.style.setProperty('--from', `${(a / this.max) * 100}%`);
    this.track.style.setProperty('--to', `${(b / this.max) * 100}%`);
  }
}
customElements.define('price-range', PriceRange);
